import { inject, Injectable, Injector } from '@angular/core';
import { EMPTY, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isActiveMasterMergeFeature } from 'src/app/config/master-merge-feature.flags';
import { DataService } from 'src/app/modules/auth/services/data.service';
import { GorevPanosuPermissionService } from '../../dashboard-task/gorev-panosu-permission.service';
import {
  ProjectDetail,
  ProjectProgressData,
  ProjectWorkflowTask,
  SaveProjectAnswerRequest,
  UpdateTaskStatusRequest,
} from '../models/project-management.model';

interface ApiResponse<T> {
  isSuccess: boolean;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectManagementService {
  private readonly injector = inject(Injector);

  constructor(private _dataService: DataService) {}

  private assertProjeYonetimiWrite(): boolean {
    return this.injector
      .get(GorevPanosuPermissionService)
      .assertProjeYonetimiWrite();
  }

  private isProjectWorkflowApiEnabled(): boolean {
    return isActiveMasterMergeFeature('projectManagement');
  }

  getProjectById(projectId: string) {
    if (!this.isProjectWorkflowApiEnabled()) {
      return of(null as unknown as ProjectDetail);
    }
    const URL = `api/ProjectWorkflow/GetProjectById?projectId=${encodeURIComponent(projectId)}`;
    return this._dataService
      .get<ApiResponse<ProjectDetail> | ProjectDetail>(URL)
      .pipe(
        map((response) => {
          const raw =
            (response as ApiResponse<ProjectDetail>)?.data ??
            (response as ProjectDetail);
          return raw as ProjectDetail;
        }),
      );
  }

  saveProjectAnswer(payload: SaveProjectAnswerRequest) {
    if (!this.isProjectWorkflowApiEnabled()) {
      return of(true);
    }
    if (!this.assertProjeYonetimiWrite()) {
      return EMPTY;
    }

    const URL = 'api/ProjectWorkflow/SaveProjectAnswer';
    return this._dataService
      .post<ApiResponse<boolean> | boolean>(URL, payload)
      .pipe(
        map((response) => {
          if (typeof response === 'boolean') return response;
          return (response as ApiResponse<boolean>)?.data ?? true;
        }),
      );
  }

  getProjectTasks(projectId: string) {
    if (!this.isProjectWorkflowApiEnabled()) {
      return of([] as ProjectWorkflowTask[]);
    }

    const URL = `api/ProjectWorkflow/GetProjectTasks?projectId=${encodeURIComponent(projectId)}`;
    return this._dataService
      .get<ApiResponse<ProjectWorkflowTask[]> | ProjectWorkflowTask[]>(URL)
      .pipe(
        map((response) => {
          const raw =
            (response as ApiResponse<ProjectWorkflowTask[]>)?.data ??
            (response as ProjectWorkflowTask[]);
          if (!Array.isArray(raw)) {
            return [];
          }
          return raw.map((task: any) => ({
            ...task,
            taskDescription:
              task?.taskDescription ?? task?.TaskDescription ?? null,
          })) as ProjectWorkflowTask[];
        }),
      );
  }

  getProjectProgress(projectId: string) {
    if (!this.isProjectWorkflowApiEnabled()) {
      return of(null as unknown as ProjectProgressData);
    }

    const URL = `api/ProjectWorkflow/GetProjectProgress?projectId=${encodeURIComponent(projectId)}`;
    return this._dataService
      .get<ApiResponse<ProjectProgressData> | ProjectProgressData>(URL)
      .pipe(
        map((response) => {
          const raw =
            (response as ApiResponse<ProjectProgressData>)?.data ??
            (response as ProjectProgressData);
          return raw as ProjectProgressData;
        }),
      );
  }

  updateTaskStatus(payload: UpdateTaskStatusRequest) {
    if (!this.isProjectWorkflowApiEnabled()) {
      return of(true);
    }
    if (!this.assertProjeYonetimiWrite()) {
      return EMPTY;
    }

    const URL = 'api/ProjectWorkflow/UpdateTaskStatus';
    return this._dataService
      .post<ApiResponse<boolean> | boolean>(URL, payload)
      .pipe(
        map((response) => {
          if (typeof response === 'boolean') return response;
          return (response as ApiResponse<boolean>)?.data ?? true;
        }),
      );
  }
}
